import { Article } from '@wix/answers-api';
import { Box, IconButton, InputArea, Text } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { ArrowRight } from '@wix/wix-ui-icons-common';
import { useState, KeyboardEvent } from 'react';

import { CHATBOT_REFERRALS } from '../../../constants';
import { useArticlePageBi } from '../../../utils/article-page-bi';
import { useStore } from '../../../zustand/stores';
import { LoginSection } from '../LoginSection';
import RelatedQuestions from '../RelatedQuestions';

import css from './index.module.scss';

type CopilotMessageInputProps = {
  relatedQuestions: string[] | undefined;
  article: Article;
  sendMessage: (message: string, forceReferral?: string) => void;
  onTextSubmit?: (text: string) => Promise<void>;
};

export const CopilotMessageInput = ({
  relatedQuestions,
  article,
  sendMessage,
  onTextSubmit,
}: CopilotMessageInputProps) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const { isChatbotInitializing, setChatbotReferral, setChatbotNodeId } = useStore(
    (state) => state.copilotSlice
  );
  const { user } = useStore((state) => state.userSlice);

  const { articleAssistantFreeTextTrack } = useArticlePageBi(article);

  const handleSendMessage = async () => {
    if (query) {
      setChatbotNodeId(null);
      setChatbotReferral(CHATBOT_REFERRALS.FREE_TEXT);
      if (onTextSubmit) {
        await onTextSubmit(query);
      } else {
        await articleAssistantFreeTextTrack(query);
      }
      sendMessage(query, CHATBOT_REFERRALS.FREE_TEXT);
      setQuery('');
    }
  };

  const handleEnterPressed = async (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.shiftKey) {
      return;
    }

    e.preventDefault();
    await handleSendMessage();
  };

  if (!user) {
    return (
      <LoginSection articleId={article.id} />
    );
  }

  return (
    <Box direction="vertical" gap="SP2">
      {relatedQuestions && relatedQuestions.length > 0 && (
        <RelatedQuestions
          relatedQuestions={relatedQuestions}
          article={article}
          sendMessage={sendMessage}
        />
      )}
      <Box direction="vertical" gap="SP1" className={css.inputAreaWrapper}>
        <Box position="relative">
          <InputArea
            onChange={(e) => setQuery(e.target.value)}
            value={query}
            autoFocus={true}
            rows={3}
            status={isChatbotInitializing ? 'loading' : undefined}
            className={css.inputArea}
            size="medium"
            placeholder={t('article-page.copilot.input.placeholder')}
            onEnterPressed={handleEnterPressed}
            forceFocus={true}
          />
          <IconButton
            onClick={handleSendMessage}
            size="small"
            className={css.inputAreaIconButton}
            skin="ai"
          >
            <ArrowRight />
          </IconButton>
        </Box>
        <Box className={css.inputAreaDisclaimer}>
          <Text size="tiny" weight="thin">
            {t('article-page.copilot.input.ai.disclaimer')}
          </Text>
        </Box>
      </Box>
    </Box>
  );
};
